import { useMutation } from '@apollo/client';
import { useCallback, useState } from 'react';
import type {
  ItemConfig,
  QuestionConfig,
  QuestionType,
  SelectOption,
  TemplateItem,
} from 'inspection-types';
import {
  type AddItemToTemplateMutation,
  type AddItemToTemplateMutationVariables,
  InspectionQuestionType,
  type InspectionTemplateItemConfigInput,
} from 'graphql-query-contracts';
import { addItemToTemplateMutation } from 'graphql-queries';

import { AddItemFromTemplateError } from './errors/AddItemFromTemplateError';

/** Domain question type keys map 1:1 to {@link InspectionQuestionType} semantics. */
function mapDomainQuestionTypeToGraphQL(
  questionType: QuestionType
): InspectionQuestionType {
  const map: Record<QuestionType, InspectionQuestionType> = {
    rating_5_point: InspectionQuestionType.Rating_5Point,
    rating_3_point: InspectionQuestionType.Rating_3Point,
    pass_fail: InspectionQuestionType.PassFail,
    ok_deficient: InspectionQuestionType.OkDeficient,
    condition_action: InspectionQuestionType.ConditionAction,
    yes_no: InspectionQuestionType.YesNo,
    checklist: InspectionQuestionType.Checklist,
    text_only: InspectionQuestionType.TextOnly,
    numeric: InspectionQuestionType.Numeric,
    date: InspectionQuestionType.Date,
    select: InspectionQuestionType.Select,
    multi_select: InspectionQuestionType.MultiSelect,
    observation: InspectionQuestionType.Observation,
  };

  return map[questionType] ?? InspectionQuestionType.PassFail;
}

function mapDomainItemConfigToInput(
  config: ItemConfig
): InspectionTemplateItemConfigInput {
  return {
    requirePhotoOnDeficiency: config.requirePhotoOnDeficiency ?? undefined,
    requireNoteOnDeficiency: config.requireNoteOnDeficiency ?? undefined,
    minPhotos: config.minPhotos ?? undefined,
    isComplianceItem: config.isComplianceItem,
    tags: config.tags ?? undefined,
    category: config.category ?? undefined,
  };
}

/**
 * Serialized shape for the InspectionQuestionConfig GraphQL scalar. Align with the
 * server-side mapper that parses incoming JSON (lane-server templateMapper).
 */
type InspectionNumericQuestionConfigInput = {
  type: 'numeric';
  min?: number;
  max?: number;
  unit?: string;
  step?: number;
  precision?: number;
};

type InspectionSelectQuestionConfigInput = {
  type: 'select' | 'multi_select';
  options: InspectionSelectOptionInput[];
};

type InspectionDateQuestionConfigInput = {
  type: 'date';
  minDate?: string;
  maxDate?: string;
  includeTime?: boolean;
};

type InspectionSelectOptionInput = {
  value: string;
  label: string;
  isDeficient: boolean;
  order: number;
};

type InspectionQuestionConfigInput =
  | InspectionNumericQuestionConfigInput
  | InspectionSelectQuestionConfigInput
  | InspectionDateQuestionConfigInput;

function mapSelectOptionToInspectionInput(
  option: SelectOption
): InspectionSelectOptionInput {
  return {
    value: option.value,
    label: option.label,
    isDeficient: option.isDeficient,
    order: option.order,
  };
}

/** Maps domain question config into the InspectionQuestionConfig wire shape. */
function mapDomainQuestionConfigToInspectionInput(
  config: QuestionConfig
): InspectionQuestionConfigInput {
  switch (config.type) {
    case 'numeric':
      return {
        type: 'numeric',
        min: config.min,
        max: config.max,
        unit: config.unit,
        step: config.step,
        precision: config.precision,
      };
    case 'select':
    case 'multi_select':
      return {
        type: config.type,
        options: config.options.map(mapSelectOptionToInspectionInput),
      };
    case 'date':
      return {
        type: 'date',
        minDate: config.minDate,
        maxDate: config.maxDate,
        includeTime: config.includeTime,
      };
    default: {
      const _unreachable: never = config;
      return _unreachable;
    }
  }
}

export function buildAddItemToTemplateVariables(params: {
  channelId: string;
  templateId: string;
  sectionId: string;
  item: TemplateItem;
}): AddItemToTemplateMutationVariables {
  const { channelId, templateId, sectionId, item } = params;

  return {
    input: {
      channelId,
      templateId,
      sectionId,
      itemId: item.id,
      label: item.label,
      helpText: item.helpText ?? undefined,
      order: item.order,
      questionType: mapDomainQuestionTypeToGraphQL(item.questionType),
      questionConfig: item.questionConfig
        ? mapDomainQuestionConfigToInspectionInput(item.questionConfig)
        : undefined,
      required: item.required,
      photosEnabled: item.photosEnabled,
      notesEnabled: item.notesEnabled,
      itemConfig: item.config
        ? mapDomainItemConfigToInput(item.config)
        : undefined,
    },
  };
}

export type AddItemToTemplateParams = {
  channelId: string;
  templateId: string;
  sectionId: string;
  item: TemplateItem;
};

export type AddItemToTemplateResult = {
  success: boolean;
  newVersion: number;
};

export type UseAddItemFromTemplateResult = {
  addItemFromTemplate: (
    params: AddItemToTemplateParams
  ) => Promise<AddItemToTemplateResult>;
  loading: boolean;
  error: Error | undefined;
};

function validateParams(params: AddItemToTemplateParams): void {
  if (!params.channelId?.trim()) {
    throw new AddItemFromTemplateError('channelId is required');
  }

  if (!params.templateId?.trim()) {
    throw new AddItemFromTemplateError('templateId is required');
  }

  if (!params.sectionId?.trim()) {
    throw new AddItemFromTemplateError('sectionId is required');
  }

  if (!params.item?.id?.trim()) {
    throw new AddItemFromTemplateError('item.id is required');
  }
}

export const useAddItemFromTemplate = (): UseAddItemFromTemplateResult => {
  const [addItem, { loading }] = useMutation<
    AddItemToTemplateMutation,
    AddItemToTemplateMutationVariables
  >(addItemToTemplateMutation);

  const [error, setError] = useState<Error | undefined>(undefined);

  const addItemFromTemplate = useCallback(
    async (
      params: AddItemToTemplateParams
    ): Promise<AddItemToTemplateResult> => {
      try {
        setError(undefined);

        validateParams(params);

        const variables = buildAddItemToTemplateVariables(params);

        const mutationResult = await addItem({ variables });

        const payload = mutationResult.data?.addItemToTemplate;

        if (!payload) {
          throw new AddItemFromTemplateError();
        }

        if (!payload.success) {
          throw new AddItemFromTemplateError(
            'Add item operation did not succeed'
          );
        }

        return {
          success: payload.success,
          newVersion: payload.newVersion,
        };
      } catch (err) {
        const wrappedError =
          err instanceof AddItemFromTemplateError
            ? err
            : new AddItemFromTemplateError(
                err instanceof Error
                  ? err.message
                  : 'Failed to add item to inspection template',
                { cause: err }
              );

        setError(wrappedError);

        throw wrappedError;
      }
    },
    [addItem]
  );

  return {
    addItemFromTemplate,
    loading,
    error,
  };
};
